/**
 * Pricing Configuration
 *
 * This file contains all pricing rules for the floor plan services.
 * Prices are in credits and will be converted to EUR for display.
 *
 * TODO: When backend API is ready, these values should be fetched from the API
 * instead of being hardcoded here.
 */

export type ServiceType = "salesOptimized" | "sized";

export interface PricingConfig {
  basePrice: number; // Base price in credits
  pricePerFloor: number; // Additional price per floor in credits
  options: Record<string, OptionPrice>;
}

export interface OptionPrice {
  pricePerFloor?: number; // Price per floor in credits
  pricePerOrder?: number; // Flat price per order in credits
}

/**
 * Sales Optimized Floor Plan Pricing
 * Base: 0 credits base, 14 credits per floor (average of 11.97-15.97)
 * Based on pricing table: Creation costs 11,97 - 15,97 per floor
 *
 * @deprecated Use pricingConfigFromCatalog() with the catalog API instead. Removed in V2.
 */
// @deprecated Use pricingConfigFromCatalog() with the catalog API instead. Removed in V2.
export const salesOptimizedPricing: PricingConfig = {
  basePrice: 0,
  pricePerFloor: 14, // Average of 11.97-15.97 EUR per floor
  options: {
    // Keys match option slugs from orderOptions.ts (also used as i18n keys)
    dimensionalChain: {
      pricePerFloor: 4.5, // Average of 3.78-5.04 EUR per floor
    },
    threeSixtyCreation: {
      pricePerFloor: 17.5, // Average of 15.13-20.17 EUR per floor
    },
    dwgFile: {
      pricePerFloor: 17.5, // Average of 15.13-20.17 EUR per floor
    },
    locationMap: {
      pricePerOrder: 17.5, // Average of 15.13-20.17 EUR per order
    },
  },
};

/**
 * Sized Floor Plan Pricing
 * Base: 0 credits base, 14 credits per floor (average of 11.97-15.97)
 * Based on pricing table: Creation costs 11,97 - 15,97 per floor
 *
 * @deprecated Use pricingConfigFromCatalog() with the catalog API instead. Removed in V2.
 */
// @deprecated Use pricingConfigFromCatalog() with the catalog API instead. Removed in V2.
export const sizedPricing: PricingConfig = {
  basePrice: 0,
  pricePerFloor: 14, // Average of 11.97-15.97 EUR per floor
  options: {
    // Keys match option slugs from orderOptions.ts (also used as i18n keys)
    livingSpaceCalculation: {
      pricePerFloor: 8.8, // Average of 7.56-10.08 EUR per floor
    },
    architectConfirmation: {
      pricePerFloor: 17.5, // Average of 15.13-20.17 EUR per floor
    },
    threeSixtyCreation: {
      pricePerFloor: 42.5, // Average of 36.55-48.74 EUR per floor
    },
    matterportTransfer: {
      pricePerOrder: 61, // Average of 52.31-69.75 EUR per order
    },
    livingSpaceArchitectConfirmation: {
      pricePerOrder: 33.8, // Average of 28.99-38.66 EUR per order
      pricePerFloor: 8.8, // Average of 7.56-10.08 EUR per floor
    },
    dwgFile: {
      pricePerFloor: 17.5, // Average of 15.13-20.17 EUR per floor
    },
  },
};

/**
 * Credit to EUR conversion rate
 * 1 credit = 1 EUR (including 19% VAT)
 */
export const CREDIT_TO_EUR = 1.0;

/**
 * VAT rate (19%)
 */
export const VAT_RATE = 0.19;

/**
 * Measurement Service Pricing Configuration
 *
 * This is a more complex pricing model based on:
 * - Property type (single family vs multi family)
 * - Number of floors
 * - Square meters
 * - Living units
 * - Additional options
 */

export interface MeasurementServiceData {
  propertyType: "singleFamilyHouse" | "multiFamilyHouse";
  floors: number;
  squareMeter: number;
  livingUnits: number;
  additionalLivingSpaceCalculation: boolean;
  cutNeeded: boolean;
  dwgFile: boolean;
  extraViews: string; // 'no', 'oneView', 'twoViews', 'threeViews', 'fourViews'
  matterport: string; // 'no', 'forThreeMonth', 'forSixMonth'
}

export interface MeasurementServicePricingConfig {
  basePrice: number;
  includedSquareMeters: number;
  additionalSquareMeterPrice: number;
  levelPriceAfterFirstLevel: number;
  flatNumberPrice: number;
  additionalLivingSpaceCalculationResidential: number;
  additionalLivingSpaceCalculationCommercial: number;
  cutResidential: number;
  cutCommercial: number;
  viewsResidential: number;
  viewsCommercial: number;
  dwgFile: number;
  matterportThreeMonths: number;
  matterportSixMonths: number;
}

/**
 * Measurement Service Base Pricing
 * Mirrors the seeded default-contract catalog as a safe fallback when the
 * public catalog cannot be fetched.
 */
export const measurementServicePricing: MeasurementServicePricingConfig = {
  basePrice: 415,
  includedSquareMeters: 150,
  additionalSquareMeterPrice: 1,
  levelPriceAfterFirstLevel: 265,
  flatNumberPrice: 115,
  additionalLivingSpaceCalculationResidential: 79,
  additionalLivingSpaceCalculationCommercial: 300,
  cutResidential: 79,
  cutCommercial: 150,
  viewsResidential: 79,
  viewsCommercial: 150,
  dwgFile: 0,
  matterportThreeMonths: 96,
  matterportSixMonths: 174,
};
